import { useState, useEffect, useRef } from "react";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { WhyGreenTariff } from "./components/WhyGreenTariff";
import { RenewableEnergySources } from "./components/RenewableEnergySources";
import { Partners } from "./components/Partners";
import { EnergyBreakdown } from "./components/EnergyBreakdown";
import { ComplianceCards } from "./components/ComplianceCards";
import { OnboardingSteps } from "./components/OnboardingSteps";
import { CertificatePreview } from "./components/CertificatePreview";
import { ContactSection } from "./components/ContactSection";
import { CTASection } from "./components/CTASection";
import { Footer } from "./components/Footer";
import { VerifyPage } from "./components/VerifyPage";
import { ReportPage } from "./components/ReportPage";
import { QuadrantStrategy } from "./components/QuadrantStrategy";
import UploadBills from "./components/UploadBills/UploadBills";
import ReportPage2 from "./components/ReportPage2";
import { GreenEnergySurvey } from "./components/GreenEnergySurvey/GreenEnergySurvey"; // 1. Import
import { GreenEnergyCredential } from "./components/GreenEnergyCredential";
import { decryptCredentials } from "./api/greenEnergy";
import { Toaster } from "./components/ui/sonner";

export default function App() {
  // Simple client-side routing
  const [currentPage, setCurrentPage] = useState(
    window.location.pathname === "/verify" ? "verify" : 
    window.location.pathname === "/report" ? "report" :  
    window.location.pathname === "/upload-files" ? "upload-files" : 
    window.location.pathname === "/green-energy-survey" ? "green-energy-survey" :
    "home"
  );

  // Credential callback handling
  const [credentialData, setCredentialData] = useState<any>(null);
  const [credentialError, setCredentialError] = useState<string | null>(null);
  const [credentialLoading, setCredentialLoading] = useState(false);
  const callbackProcessed = useRef(false);

  // Detect hash callback on mount
  const [callbackParams] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith("#switch-to-green-energy?")) return null;
    const queryString = hash.split("?")[1];
    const params = new URLSearchParams(queryString);
    return {
      request_id: params.get("request_id") || "",
      status: params.get("status") || "",
      credential: params.get("credential") || "",
      meter_credential: params.get("meter_credential") || "",
      errorMsg: params.get("errorMsg") || "",
    };
  });

  // Process callback on mount (once)
  useEffect(() => {
    if (callbackParams && !callbackProcessed.current) {
      callbackProcessed.current = true;
      // Clean URL immediately
      window.history.replaceState({}, "", "/");
      // Call decrypt API
      setCredentialLoading(true);
      decryptCredentials({
        request_id: callbackParams.request_id,
        status: callbackParams.status,
        credential: callbackParams.credential || undefined,
        meter_credential: callbackParams.meter_credential || undefined,
        errorMsg: callbackParams.errorMsg || undefined,
      })
        .then((response) => {
          setCredentialData(response.data);
        })
        .catch((err: any) => {
          setCredentialError(
            err.response?.data?.message ||
              err.message ||
              "Failed to decrypt credentials"
          );
        })
        .finally(() => {
          setCredentialLoading(false);
        });
    }
  }, [callbackParams]);

  // Handle navigation
  const handleNavigation = (page: string) => {
    setCurrentPage(page);
    window.history.pushState({}, "", page === "home" ? "/" : `/${page}`);
  };

  // Listen for browser back/forward
  window.onpopstate = () => {
    setCurrentPage(
      window.location.pathname === "/verify" ? "verify" : 
      window.location.pathname === "/report" ? "report" : 
      window.location.pathname === "/upload-files" ? "upload-files" : 
      window.location.pathname === "/green-energy-survey" ? "green-energy-survey" : 
      "home"
    );
  };

  // Override link clicks
  const handleLinkClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const link = target.closest("a");
    if (link && link.href.includes("/verify")) {
      e.preventDefault();
      handleNavigation("verify");
    } else if (link && link.href.includes("/report")) {
      e.preventDefault();
      handleNavigation("report");
    }
    else if (link && link.href.includes("/upload-files")) {
      e.preventDefault();
      handleNavigation("upload-files");
    } 
    else if (link && link.href.includes("/green-energy-survey")) {
      e.preventDefault();
      handleNavigation("green-energy-survey");
    } 
    else if (link && link.href === window.location.origin + "/") {
      e.preventDefault();
      handleNavigation("home");
    }
  };

  // Add click listener
  if (typeof document !== "undefined") {
    document.addEventListener("click", handleLinkClick);
  }

  // Credential callback page
  if (credentialLoading || credentialData || credentialError) {
    return (
      <GreenEnergyCredential
        data={credentialData}
        error={credentialError}
        loading={credentialLoading}
      />
    );
  }

  if (currentPage === "verify") {
    return <VerifyPage />;
  }

  if (currentPage === "report") {
    return <ReportPage2 />;
  }

  if (currentPage === "upload-files") {
    return <UploadBills />;
  }

  // 5. Render Page
  if (currentPage === "green-energy-survey") {
    return <GreenEnergySurvey />;
  }

  console.log({currentPage});
  
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Hero />
      <WhyGreenTariff />
      <QuadrantStrategy />
      <EnergyBreakdown />
      <RenewableEnergySources />
      <Partners />
      <ComplianceCards />
      <OnboardingSteps />
      <CertificatePreview />
      <ContactSection />
      <CTASection />
      <Footer />
      <Toaster />
      {/* <UploadBills /> */}
    </div>
  );
}